import projectThumbnailSrc from '@assets/images/project-thumbnail.png';
import {
  Button,
  Divider,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  Image,
  RiEditLine,
  RiMore2Line,
  Shimmer,
  Stack,
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from 'component-library';
import { FC, useMemo, useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { MembershipWithOrganizationTypeEnum } from '@/api/rest/resources/types/membership';
import { OrganizationTypeEnum } from '@/api/rest/resources/types/organization';
import { PlotType } from '@/api/rest/resources/types/plot';
import {
  Project,
  ProjectBuyerDetail,
  ProjectDetail,
  ProjectStatus,
  ProjectType,
} from '@/api/rest/resources/types/project';
import {
  CopyLandStewardEmailButton,
  PlotTypeIcon,
  ProjectStatusBadge,
  ProjectStatusDemoBadge,
  withSuspenseBoundary,
} from '@/components';
import { AgricultureProjectThumbnail, ConservationProjectThumbnail } from '@/components/MapThumbnail/MapThumbnail';
import { PendingPill } from '@/components/Pill/PendingPill';
import { useMembershipType } from '@/hooks/useMembershipType';
import { EditProject } from '@/pages/shared/components/EditProject';
import { usePlotsForProject } from '@/pages/shared/hooks/usePlotsForProject';
import { useProject } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useProject';
import { useProjectBoundary } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useProjectPolygon';
import { Area, useAreaDisplay } from '@/utils/area';
import { formatUnit } from '@/utils/formatting';
import { getProjectPermissions } from '@/utils/permissions/getProjectPermissions';

import { DataLabel, DataStack, DataText } from './DataComponents';

export const ProjectCardMobile: FC<{ project: Project }> = ({ project }) => {
  const projectStatus = project.status;

  const membershipType = useMembershipType();

  const showCompleteInfo = useMemo(() => {
    if (membershipType === MembershipWithOrganizationTypeEnum.land_steward) return true;

    return project.organization.type === OrganizationTypeEnum.DEMO || projectStatus !== ProjectStatus.initial;
  }, [membershipType, project.organization.type, projectStatus]);

  return (
    <article
      data-testid='projectcard-mobile'
      className='relative grid w-full grid-cols-[24px_1fr_24px] overflow-hidden rounded-2xl bg-white-100'
    >
      {showCompleteInfo ? <CompleteInfo project={project} /> : <MinimalInfo project={project} />}
    </article>
  );
};

const MinimalInfo = ({ project }: { project: Project }) => {
  const { t } = useTranslation();

  const invitationDate = (project as ProjectBuyerDetail).invited_at;

  return (
    <>
      <Stack direction='row' className='items-center justify-between border-b border-divider p-6 full-bleed-x'>
        <Stack direction='row' className='flex min-w-0 items-center gap-6'>
          <Thumbnail project={project} />
          <DataStack>
            <DataLabel>{t('shared.projects.overviewCard.labels.landSteward')}</DataLabel>
            <DataText>{project.organization.name}</DataText>
          </DataStack>
        </Stack>
      </Stack>
      <div className='col-start-2 flex min-w-0 flex-col gap-10 py-6'>
        <DataStack>
          <DataLabel>{t('shared.projects.overviewCard.labels.invitationDate')}</DataLabel>
          <DataText>{invitationDate ? new Date(invitationDate).toLocaleDateString() : '--'}</DataText>
        </DataStack>
        <CopyLandStewardEmailButton email={(project as ProjectBuyerDetail).organization_email} />
      </div>
    </>
  );
};

const CompleteInfo = ({ project }: { project: Project }) => {
  const { t } = useTranslation();

  const membershipType = useMembershipType();

  const plotTypes = project.landtypes_allowed;

  const isDemoProject = project.organization.type === OrganizationTypeEnum.DEMO;
  const hasWritePermission = getProjectPermissions(project).includes('write');

  const [isEditingProject, setIsEditingProject] = useState(false);
  return (
    <>
      <div className='flex items-center justify-between p-6 pb-0 full-bleed-x'>
        <div className='flex items-center gap-6'>
          <Thumbnail project={project} />
          <DataStack>
            <DataLabel>{t('shared.projects.overviewCard.labels.projectName')}</DataLabel>
            <DataText>{project.name}</DataText>
          </DataStack>
        </div>
      </div>

      <div className='col-start-2 flex min-w-0 flex-col gap-6 py-6'>
        <Divider />

        {(() => {
          if (project.type === ProjectType.conservation) {
            return (
              <DataStack>
                <DataLabel>{t('shared.projects.overviewCard.labels.management')}</DataLabel>
                <DataText>{project.organization.name ?? '--'}</DataText>
              </DataStack>
            );
          }

          if (membershipType === MembershipWithOrganizationTypeEnum.land_steward) {
            return (
              <DataStack>
                <DataLabel>{t('shared.projects.overviewCard.labels.sponsor')}</DataLabel>
                <DataText>{(project as ProjectDetail).buyer?.name ?? '--'}</DataText>
              </DataStack>
            );
          }

          return (
            <DataStack>
              <DataLabel>{t('shared.projects.overviewCard.labels.landSteward')}</DataLabel>
              <DataText>{project.organization.name ?? '--'}</DataText>
            </DataStack>
          );
        })()}
        <Divider />

        <DataStack>
          <DataLabel>{t('shared.projects.overviewCard.labels.location')}</DataLabel>
          <DataText>
            <ProjectLocation project={project} />
          </DataText>
        </DataStack>

        <Divider />
        <DataStack>
          <DataLabel>{t('shared.projects.overviewCard.labels.area')}</DataLabel>
          <DataText>
            <ProjectArea project={project} />
          </DataText>
        </DataStack>
        <Divider />

        {(() => {
          if (project.type === ProjectType.conservation) {
            return (
              <>
                <DataStack>
                  <DataLabel>{t('shared.projects.overviewCard.labels.projectType')}</DataLabel>
                  <DataText>{t('shared.projects.projectType.conservation')}</DataText>
                </DataStack>
              </>
            );
          }

          if (plotTypes.length > 0) {
            return (
              <>
                <DataStack>
                  <DataLabel>{t('shared.projects.overviewCard.labels.projectLandType')}</DataLabel>
                  <DataText>
                    {plotTypes.length > 1 ? (
                      t('shared.projects.overviewCard.mixedLandtypes')
                    ) : (
                      <Stack direction='row' spacing={1} title={t(`global.plotTypes.${plotTypes[0] as PlotType}`)}>
                        <PlotTypeIcon type={plotTypes[0] as PlotType} size={20} />
                        <span>{t(`global.plotTypes.${plotTypes[0] as PlotType}`)}</span>
                      </Stack>
                    )}
                  </DataText>
                </DataStack>
              </>
            );
          }

          return null;
        })()}

        {project.type === ProjectType.agriculture && (
          <>
            <Divider />
            <DataStack>
              <DataLabel>{t('shared.projects.overviewCard.labels.projectStatus')}</DataLabel>
              {isDemoProject ? <ProjectStatusDemoBadge /> : <ProjectStatusBadge project={project} />}
            </DataStack>
          </>
        )}

        <Stack direction='row' spacing={2} className='mt-4 flex-1'>
          <Button variant='outline' className='flex-1'>
            {t('shared.projects.overviewCard.labels.seeDetailsButton')}
          </Button>

          {membershipType === MembershipWithOrganizationTypeEnum.land_steward && (
            <Dropdown modal={true}>
              <DropdownTrigger data-testid='project-overflow-menu'>
                <div className='shrink-0 rounded-full border border-primary-100 p-2 text-primary-100 hover:border-primary-hover hover:bg-primary-12'>
                  <RiMore2Line size={24} />
                </div>
              </DropdownTrigger>
              <DropdownContent side='bottom' sideOffset={10}>
                {hasWritePermission ? (
                  <DropdownItem leftAdornment={<RiEditLine />} onClick={() => setIsEditingProject(true)}>
                    {t('shared.projects.overviewCard.labels.editProject')}
                  </DropdownItem>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownItem disabled>{t('shared.projects.overviewCard.labels.editProject')}</DropdownItem>
                    </TooltipTrigger>
                    <TooltipContent sideOffset={5} side='left'>
                      {t('global.tooltips.disabledLockedProject')}
                      <TooltipArrow />
                    </TooltipContent>
                  </Tooltip>
                )}
              </DropdownContent>
            </Dropdown>
          )}
        </Stack>

        <EditProject projectId={project.id} open={isEditingProject} onOpenChange={(o) => setIsEditingProject(o)} />
      </div>
    </>
  );
};

const ProjectLocation = ({ project }: { project: Project }) => {
  if (project.type === ProjectType.conservation) {
    return <ConservationProjectLocation project={project} />;
  }
  if (project.type === ProjectType.agriculture) {
    return <AgricultureProjectLocation project={project} />;
  }

  return <UnknownLocation />;
};

const UnknownLocation = () => {
  const { t } = useTranslation();

  return <>{t('shared.projects.overviewCard.unknownLocation')}</>;
};

const AgricultureProjectLocation = ({ project }: { project: Project }) => {
  const projectLocation = project.location_description;

  if (!projectLocation) {
    return <UnknownLocation />;
  }

  return <>{projectLocation}</>;
};

const ConservationProjectLocation = withErrorBoundary<{ project: Project }>(
  withSuspenseBoundary(
    ({ project }) => {
      const projectLocation = useProject(project.id)?.location_description;

      if (!projectLocation) {
        return <UnknownLocation />;
      }

      return <>{projectLocation}</>;
    },
    <PendingPill />,
  ),
  { fallback: <UnknownLocation /> },
);

const ProjectArea = ({ project }: { project: Project }) => {
  if (project.type === ProjectType.conservation) {
    return <ConservationProjectArea project={project} />;
  }
  if (project.type === ProjectType.agriculture) {
    return <AgricultureProjectArea project={project} />;
  }

  return <UnknownArea />;
};

const UnknownArea = () => {
  return <>--</>;
};

const AgricultureProjectArea = ({ project }: { project: Project }) => {
  const areaDisplay = useAreaDisplay(new Area({ value: project.area, dimension: 'squareMeters' }).toHectares());

  if (project.area <= 0) {
    return <UnknownArea />;
  }

  return <>{areaDisplay}</>;
};

const ConservationProjectArea = withErrorBoundary<{ project: Project }>(
  withSuspenseBoundary(
    ({ project }) => {
      const projectData = useProject(project.id);
      const projectArea = projectData?.area;
      const areaUnits = formatUnit(projectData?.areaUnits) ?? '';

      return <>{`${projectArea} ${areaUnits}`}</>;
    },
    <PendingPill />,
  ),
  { fallback: <UnknownArea /> },
);

const Thumbnail = withErrorBoundary<{ project: Project }>(
  withSuspenseBoundary(
    ({ project }) => {
      if (project.type === ProjectType.conservation) {
        return <ConservationThumbnail projectId={project.id} />;
      }

      return <AgricultureThumbnail projectId={project.id} />;
    },
    <Shimmer className='h-20 w-20 rounded-2xl' />,
  ),
  { fallback: <Image src={projectThumbnailSrc} className='h-20 w-20 rounded-2xl' alt='project-thumbnail' /> },
);

const ConservationThumbnail = ({ projectId }: { projectId: string }) => {
  const polygons = useProjectBoundary(projectId);

  if (!polygons) return null;

  return <ConservationProjectThumbnail polygons={polygons} className='h-20 w-20 rounded-2xl' />;
};

const AgricultureThumbnail = ({ projectId }: { projectId: string }) => {
  const plots = usePlotsForProject(projectId).data.results;

  if (!plots) {
    return <Image src={projectThumbnailSrc} className='h-20 w-20 rounded-2xl' alt='project-thumbnail' />;
  }

  return <AgricultureProjectThumbnail plots={plots} className='h-20 w-20 rounded-2xl' />;
};
